import { reactRouterParameters } from "storybook-addon-remix-react-router"

import TimezoneProvider from "../../../lib/TimezoneProvider"
import ViewportProvider from "../../../lib/ViewportProvider"
import Sidebar, { Tabs } from "./Sidebar"
import { buildClientRecord, buildContractRecord, buildVendorRecord } from "lib/storybook/utils"

export default {
  title: "UI/Sidebar",
  component: Sidebar,
}

const Template = (args) => {
  return (
    <ViewportProvider>
      <TimezoneProvider>
        <Sidebar
          authenticityToken="something"
          {...args}
          sx={{
            "& .MuiPaper-root": {
              position: 'static',
            },
          }}
        />
      </TimezoneProvider>
    </ViewportProvider>
  )
}

function buildRouteTree({ hasGlobalDashboard, permitSystemAdmin, permitOrganizationAdmin, permitLandingPageEditor, hasClients, hasVendors }) {
  return {
    path: "/",
    children: [
      { index: true, useStoryElement: true },
      hasGlobalDashboard && { path: 'dashboard', handle: { tab: Tabs.Global.Dashboard }, useStoryElement: true },
      hasClients && { path: 'clients/:clientId/dashboard', handle: { tab: Tabs.Client.Dashboard }, useStoryElement: true },
      hasVendors && { path: 'contracts/:contractId/dashboard', handle: { tab: Tabs.Vendor.Dashboard }, useStoryElement: true },
      permitSystemAdmin && { path: 'system/clients', handle: { tab: Tabs.Global.Clients }, useStoryElement: true },
      permitSystemAdmin && { path: 'system/vendors', handle: { tab: Tabs.Global.Vendors }, useStoryElement: true },
      permitLandingPageEditor && { path: 'vendors/:vendorId/landing-pages', handle: { tab: Tabs.Vendor.LandingPages }, useStoryElement: true },
      permitSystemAdmin && { path: 'system/organizations', handle: { tab: Tabs.Global.Organizations }, useStoryElement: true },
      permitOrganizationAdmin && { path: 'organizations/:organizationId/users', handle: { tab: Tabs.Global.Users }, useStoryElement: true },
      hasClients && { path: 'clients/:clientId', handle: { tab: Tabs.Client.Manage }, useStoryElement: true },
      hasClients && { path: 'clients/:clientId/pacing', handle: { tab: Tabs.Client.Pacing }, useStoryElement: true },
      hasVendors && { path: 'contracts/:contractId/integrations', handle: { tab: Tabs.Vendor.Integrations }, useStoryElement: true },
      hasVendors && { path: 'contracts/:contractId/allocations', handle: { tab: Tabs.Vendor.Allocations }, useStoryElement: true },
      (hasClients || hasVendors) && { path: 'leads', handle: { tab: Tabs.Global.Leads }, useStoryElement: true },
      (hasClients || hasVendors) && { path: 'performance', handle: { tab: Tabs.Global.Performance }, useStoryElement: true },
    ],
  }
}

function buildParameters({ numClients = 0, numVendors = 0, numInternalVendors = 0, permitOrganizationAdmin = false, permitSystemAdmin = false, permitLandingPageEditor = false, hasGlobalDashboard = false, path = "/leads" }) {
  return {
    reactRouter: reactRouterParameters({
      routing: buildRouteTree({ hasGlobalDashboard, permitOrganizationAdmin, permitSystemAdmin, permitLandingPageEditor , hasClients: numClients > 0, hasVendors: numVendors + numInternalVendors > 0 }),
      location: { path: path },
    }),
    orbit: {
      user: {
        permitSystemAdmin,
        permitOrganizationAdmin,
        permitLandingPageEditor,
        accessibleClientIds: ['1','2'].slice(0, numClients),
        accessibleVendorIds: [
          ...['1','2'].slice(0, numVendors),
          ...['3'].slice(0, numInternalVendors),
        ],
      },
      records: [
        numVendors > 0 && buildVendorRecord('1', 'Education Station'),
        numVendors > 1 && buildVendorRecord('2', 'LeadsMart'),
        numInternalVendors > 0 && buildVendorRecord('3', 'Internal Lead Buys'),
        buildClientRecord('1', 'Vega University'),
        buildClientRecord('2', 'School of Hard Knocks'),
        numVendors > 0 && buildContractRecord('1', '1'),
        numVendors > 1 && buildContractRecord('1', '2'),
        numVendors > 0 && buildContractRecord('2', '1'),
        numInternalVendors > 0 && buildContractRecord('1', '3'),
      ].filter(Boolean),
    },
  }
}

const Default = {
  render: Template,
  args: {
  },
  parameters: {
  },
}

export const SingleClientRegularUser = {
  ...Default,
  parameters: buildParameters({ numClients: 1, numVendors: 0 }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SingleClientAdmin = {
  ...Default,
  parameters: buildParameters({ numClients: 1, numVendors: 0, permitOrganizationAdmin: true }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SingleClientWithInternalVendor = {
  ...Default,
  parameters: buildParameters({ numClients: 1, numInternalVendors: 1, permitOrganizationAdmin: true }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SingleVendorRegularUser = {
  ...Default,
  parameters: buildParameters({ numClients: 0, numVendors: 1 }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SingleVendorWithLandingPagesRegularUser = {
  ...Default,
  parameters: buildParameters({ numClients: 0, numVendors: 1, permitLandingPageEditor: true }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SingleVendorAdmin = {
  ...Default,
  parameters: buildParameters({ numClients: 0, numVendors: 1, permitOrganizationAdmin: true }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const MultipleVendorAdmin = {
  ...Default,
  parameters: buildParameters({ numClients: 0, numVendors: 2, permitOrganizationAdmin: true }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SystemAdmin = {
  ...Default,
  parameters: buildParameters({ numClients: 0, numVendors: 2, permitOrganizationAdmin: true, permitSystemAdmin: true, hasGlobalDashboard: true }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}

export const SystemAdminCollapsed = {
  ...SystemAdmin,
  args: {
    ...SystemAdmin.args,
    initiallyExpanded: false,
  },
}

export const NoOrganizationAccess = {
  ...Default,
  parameters: buildParameters({ numClients: 0, numVendors: 0, hasGlobalDashboard: true, path: "/dashboard" }),
  args: {
    ...Default.args,
    initiallyExpanded: true,
  }
}
